import * as React from 'react';

import { enumCurrencyCode } from '@rematter/frontend-lib-api-client';
import {
  AsyncBoundary,
  ConsoleDialog,
  DataGrid,
  Pagination,
  type PaginationProps,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import { useDrawerPaymentColumns } from './hooks/useDrawerPaymentColumns';

import * as state from '@/finance/_state/financialAccountPayments.state';
import { useTranslations } from '@/finance/i18n/useTranslations';
import { FinancialAccountRecord, PaymentRecord } from '~/modules/finance/types';

type TransactionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  account?: FinancialAccountRecord;
};

export const TransactionDrawer = ({
  isOpen,
  onClose,
  account,
}: TransactionsDrawerProps) => {
  const t = useTranslations();

  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);

  const paymentsAtom = React.useMemo(
    () => state.createFinancialAccountPaymentsAtom(account?.id || ''),
    [account?.id],
  );

  const {
    data: { payments = [], total = 0 } = {},
    isLoading,
    error,
  } = useAtomValue(paymentsAtom);

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const columns = useDrawerPaymentColumns({
    currency: account?.currency ?? enumCurrencyCode.USD,
  });

  if (!isOpen || !account) {
    return null;
  }

  return (
    <ConsoleDialog
      className="@TransactionDrawer"
      isOpen={isOpen}
      onOpenChange={onClose}
      title={t.financialAccounts.transactions.drawer.title(account.name)}
      contentClassName="p-0 flex-1"
      size="fill"
      closeOnClickOutside={false}
      content={
        <AsyncBoundary loading={isLoading} error={error}>
          <DataGrid<PaymentRecord>
            columns={columns}
            dataSource={payments}
            rowKey="id"
            fullWidth
            emptyPanelProps={{
              title: t.financialAccounts.transactions.drawer.empty.title,
              description:
                t.financialAccounts.transactions.drawer.empty.description,
            }}
          />
        </AsyncBoundary>
      }
      footer={
        <Pagination
          limit={limit}
          offset={offset}
          total={total}
          onChange={handlePaginationChange}
        />
      }
    />
  );
};
